// src/services/session.ts
//
// Everything that decides whether the user is still signed in.
//
// The access token is short-lived (~15 min) and lives in localStorage; the
// refresh token is an httpOnly cookie the browser manages and this code never
// sees. A 401 carrying `token_expired` is routine — one refresh renews it and
// the failed request replays, invisibly. Only an explicit end-of-session code
// signs the user out, and only once no matter how many requests failed.

import axios, { AxiosError, AxiosInstance } from 'axios';
import toast from 'react-hot-toast';
import { stashIntendedRoute } from '../utils/intendedRoute';

/**
 * Module augmentation: extend axios' request config with our own flags so
 * callers and interceptors can use them without an `as any` cast.
 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * When true, the response interceptor will NOT toast on 4xx errors for this
     * request — used by pages (e.g. the sign-up wizard) that surface errors
     * inline. Session-ending failures still toast unconditionally.
     */
    silenceToast?: boolean;
    /** Set when a request has already been replayed after a token refresh, so
     *  a second 401 cannot loop. */
    _authRetried?: boolean;
  }
}

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.zamino.com';

const TOKEN_KEY = 'bizguard_token';
const EXPIRES_AT_KEY = 'bizguard_token_expires_at';
const WORKSPACE_KEY = 'bizguard_active_workspace';
const ADMIN_TOKEN_KEY = 'bizguard_admin_token';

/** Renew this far ahead of expiry, and treat anything inside it as due. */
const REFRESH_LEAD_MS = 60_000;

export const tokenManager = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string, expiresInSeconds?: number) => {
    localStorage.setItem(TOKEN_KEY, token);
    if (expiresInSeconds) {
      localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + expiresInSeconds * 1000));
    } else {
      // Unknown lifetime (impersonation hand-off, legacy token). Forget any
      // previous expiry rather than inheriting it — scheduling a refresh off
      // another token's clock would swap this one out from under the caller.
      localStorage.removeItem(EXPIRES_AT_KEY);
    }
    // A live token means the previous sign-out (if any) is over.
    sessionEnded = false;
    scheduleProactiveRefresh();
  },
  remove: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
    clearProactiveRefresh();
  },
};

// Active-workspace management (Workspaces Layer 4). The workspace is the single
// ambient scope: it rides every request as the X-Workspace-Id header, which the
// backend validates against the caller's workspace_users membership and turns
// into the active workspace + management role. When unset, the backend falls
// back to the identity's persisted last_active workspace. The switcher persists
// the choice here so it survives reloads. Businesses are never ambient.
export const activeWorkspaceManager = {
  get: () => localStorage.getItem(WORKSPACE_KEY),
  set: (workspaceId: string) => localStorage.setItem(WORKSPACE_KEY, workspaceId),
  remove: () => localStorage.removeItem(WORKSPACE_KEY),
};

// While a platform admin is impersonating, TOKEN_KEY holds the customer's token
// and the admin's own token is parked here. Its presence is what makes "exit"
// possible without a re-login — and what stops an expired support session from
// logging the admin out of their own account.
export const adminTokenManager = {
  get: () => localStorage.getItem(ADMIN_TOKEN_KEY),
  set: (token: string) => localStorage.setItem(ADMIN_TOKEN_KEY, token),
  remove: () => localStorage.removeItem(ADMIN_TOKEN_KEY),
};

// ---------------------------------------------------------------------------
// Server contract
// ---------------------------------------------------------------------------

/** Codes the client answers with a silent refresh rather than a sign-out. */
const RENEWABLE_CODES = new Set(['token_expired', 'token_missing']);

/**
 * Why the session ended, in the user's words. Never show a server string —
 * that is how a customer once saw the words "Invalid token".
 *
 * `tone` splits the routine deadlines from the security events: a lapsed
 * session is an inconvenience, a revoked or replayed one is something the user
 * should actually read.
 */
const SIGN_OUT_NOTICES: Record<string, { tone: SessionEndTone; message: string }> = {
  session_expired: { tone: 'info', message: 'Your session has ended. Sign in to pick up where you left off.' },
  session_idle_timeout: { tone: 'info', message: 'You were signed out after a period of inactivity.' },
  session_revoked: { tone: 'info', message: 'Your session was ended from another device or by an admin.' },
  token_reuse_detected: {
    tone: 'alert',
    message:
      'We ended your session as a precaution. Sign in again, then review your active sessions in Settings.',
  },
  account_inactive: { tone: 'alert', message: 'This account is no longer active. Contact support to restore access.' },
  account_locked: { tone: 'alert', message: 'Your account is locked. Check your email for an unlock link.' },
};
const DEFAULT_SIGN_OUT_NOTICE = { tone: 'info' as const, message: 'Please sign in to continue.' };

export type SessionEndTone = 'info' | 'alert';
export interface SessionEndNotice {
  code: string | null;
  tone: SessionEndTone;
  message: string;
}

const END_NOTICE_KEY = 'bizguard_session_end_notice';

/** Memoized so React StrictMode's double-invoked render can't eat the notice
 *  between the first read and the one that gets committed. Reset on each
 *  sign-out, which is the only thing that writes a new one. */
let readNotice: SessionEndNotice | null | undefined;

/**
 * Read and clear the reason the last session ended. The sign-in page renders
 * this inline rather than as a toast: the user is being sent to a new screen
 * anyway, and a calm line above the form reads as an explanation where a
 * bubble in the corner reads as an error.
 */
export function consumeSessionEndNotice(): SessionEndNotice | null {
  if (readNotice !== undefined) return readNotice;

  const raw = sessionStorage.getItem(END_NOTICE_KEY);
  sessionStorage.removeItem(END_NOTICE_KEY);
  try {
    readNotice = raw ? (JSON.parse(raw) as SessionEndNotice) : null;
  } catch {
    readNotice = null;
  }
  return readNotice;
}

// ---------------------------------------------------------------------------
// Sign-out — idempotent by design
// ---------------------------------------------------------------------------

/** Latches on the first failure so N concurrent 401s produce one toast, one
 *  redirect, and one stashed route instead of N of each. */
let sessionEnded = false;

export function endSession(code?: string): void {
  if (sessionEnded) return;
  sessionEnded = true;

  tokenManager.remove();
  activeWorkspaceManager.remove();

  // A replayed refresh token means the credential leaked. Leave nothing behind
  // that identifies the account on this device.
  if (code === 'token_reuse_detected') {
    adminTokenManager.remove();
    sessionStorage.clear();
  }

  // Remember where they were so sign-in returns them there, not to /home.
  const here = window.location.pathname + window.location.search;
  if (here && !here.startsWith('/login')) stashIntendedRoute(here);

  const { tone, message } = (code && SIGN_OUT_NOTICES[code]) || DEFAULT_SIGN_OUT_NOTICE;
  // Handed to the sign-in page instead of toasted — see consumeSessionEndNotice.
  sessionStorage.setItem(
    END_NOTICE_KEY,
    JSON.stringify({ code: code ?? null, tone, message } satisfies SessionEndNotice),
  );
  readNotice = undefined;

  window.dispatchEvent(
    new CustomEvent('auth:unauthorized', { detail: { code: code ?? null, message } }),
  );
}

// ---------------------------------------------------------------------------
// Refresh — single-flight
// ---------------------------------------------------------------------------

type RefreshOutcome =
  | { ok: true; token: string }
  /** `retryable` separates "the session is over" from "the server blinked".
   *  Only the former may sign anyone out. */
  | { ok: false; retryable: boolean };

let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Exchange the refresh cookie for a new access token. Concurrent callers share
 * one request — three parallel 401s must not become three refreshes, or
 * rotation would treat two of them as replays.
 */
export function refreshAccessToken(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function performRefresh(): Promise<RefreshOutcome> {
  try {
    const { data } = await axios.post<{ token: string; expiresIn: number }>(
      `${API_BASE_URL}/api/auth/refresh`,
      null,
      { withCredentials: true, timeout: 20000 },
    );
    tokenManager.set(data.token, data.expiresIn);
    return { ok: true, token: data.token };
  } catch (error) {
    const response = (error as AxiosError<{ code?: string }>).response;

    // No response at all, or the auth service is down: the session may well be
    // fine. Leave the token alone and let the caller retry.
    if (!response || response.status >= 500) {
      return { ok: false, retryable: true };
    }

    endSession(response.data?.code);
    return { ok: false, retryable: false };
  }
}

/**
 * Renew the token if it is within the lead window. Cheap and synchronous in the
 * common case — for raw `fetch()` callers that bypass the axios interceptors.
 */
export async function ensureFreshAccessToken(): Promise<void> {
  if (!tokenManager.get() || !isRefreshDue()) return;
  await refreshAccessToken();
}

function expiresAt(): number | null {
  const raw = localStorage.getItem(EXPIRES_AT_KEY);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

function isRefreshDue(): boolean {
  const at = expiresAt();
  // Tokens issued before this contract have no recorded expiry. Don't
  // pre-emptively refresh them; the 401 path picks them up.
  return at !== null && at - Date.now() <= REFRESH_LEAD_MS;
}

// ---------------------------------------------------------------------------
// Proactive renewal — the reason a tab left open overnight still works
// ---------------------------------------------------------------------------

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function clearProactiveRefresh(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
}

function scheduleProactiveRefresh(): void {
  clearProactiveRefresh();
  const at = expiresAt();
  if (at === null) return;

  refreshTimer = setTimeout(
    () => {
      // A background tab's timer is throttled and its refresh would race every
      // other tab's. Let the visibility handler take it when the user returns.
      if (document.visibilityState === 'visible') void refreshAccessToken();
    },
    Math.max(at - Date.now() - REFRESH_LEAD_MS, 1000),
  );
}

/**
 * Install the lifecycle hooks. Called once from the app root: a tab that slept
 * through its own expiry renews on the way back, before any query fires — which
 * is what turns "left it open overnight" into a non-event.
 */
export function initSessionLifecycle(): () => void {
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    if (!tokenManager.get()) return;
    if (isRefreshDue()) void refreshAccessToken();
    else scheduleProactiveRefresh();
  };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);
  onVisible();

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onVisible);
    clearProactiveRefresh();
  };
}

// ---------------------------------------------------------------------------
// Interceptors — one implementation, applied to every axios instance
// ---------------------------------------------------------------------------

export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'X-Timezone': getApiTimezone(),
  };
  const token = tokenManager.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  const workspaceId = activeWorkspaceManager.get();
  if (workspaceId) headers['X-Workspace-Id'] = workspaceId;
  return headers;
}

// Timezone bridged from TimezoneContext (honors the user's UI override).
// Resolved fresh from the browser on every request when no override is set,
// so traveling users always send their current IANA zone.
let currentTimezone: string | null = null;

export const setApiTimezone = (timezone: string | null): void => {
  currentTimezone = timezone;
};

export const getApiTimezone = (): string =>
  currentTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Attach request + response handling to an axios instance. Every instance in
 * the app gets this: a client without it reports an expired token as a generic
 * failure and shows the raw server string to the user.
 */
export function attachAuthInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    for (const [name, value] of Object.entries(authHeaders())) {
      config.headers.set(name, value);
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiErrorBody>) => handleResponseError(instance, error),
  );
}

interface ApiErrorBody {
  code?: string;
  error?: string;
  message?: string;
}

async function handleResponseError(
  instance: AxiosInstance,
  error: AxiosError<ApiErrorBody>,
): Promise<never> {
  const status = error.response?.status;
  const data = error.response?.data;
  const code = data?.code;
  const config = error.config;
  const silenced = !!config?.silenceToast;

  // The auth service itself is struggling. This is an availability problem, not
  // a credential one — signing the user out here would turn a blip into an
  // outage for everyone holding a token.
  if (status === 503 && code === 'auth_unavailable') {
    toast.error('Reconnecting to Zamino…', { id: 'auth-unavailable' });
    throw error;
  }

  if (status === 401) {
    const adminToken = adminTokenManager.get();
    if (adminToken) {
      // A support session lapsed (they are capped at 1h and cannot refresh).
      // Put the admin back in their own seat rather than at the login screen.
      tokenManager.set(adminToken);
      adminTokenManager.remove();
      activeWorkspaceManager.remove();
      toast.error('Support session ended — you are back in your admin account.', {
        id: 'session-ended',
      });
      window.location.href = '/admin/users';
      throw error;
    }

    if (config && !config._authRetried && RENEWABLE_CODES.has(code ?? '')) {
      const outcome = await refreshAccessToken();
      if (outcome.ok) {
        config._authRetried = true;
        // The request interceptor re-reads the token from storage on replay.
        return instance.request(config) as never;
      }
      if (outcome.retryable) {
        toast.error('Reconnecting to Zamino…', { id: 'auth-unavailable' });
        throw error;
      }
      // endSession already ran inside performRefresh.
      throw error;
    }

    endSession(code);
    throw error;
  }

  if (status === 403 && data?.error === 'workspace_required') {
    // The identity lost its last membership (or never had one) since /me was
    // cached. Re-hydrate the identity; ProtectedRoute then sends it to /welcome.
    window.dispatchEvent(new CustomEvent('auth:workspace_required'));
    throw error;
  }

  if (status === 429 && data?.error === 'usage_limit_exceeded') {
    toast.error(data.message || 'Usage limit reached.', { id: 'usage-limit' });
    window.dispatchEvent(new CustomEvent('usage:limit_exceeded'));
    throw error;
  }

  if (!silenced && status !== undefined) {
    if (status >= 500) toast.error('Server error. Please try again later.', { id: 'server-error' });
    else if (status >= 400) toast.error(data?.error || error.message || 'An error occurred');
  }

  throw error;
}
